// ProductDetails.js
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./ProductDetails.css";
import { emitAddedToCart } from "../../components/AddedToCart/AddedToCart";
import { SkeletonProduct } from "../../components/Skeleton/Skeleton";
import RelatedProduct from "../RelatedProduct/RelatedProduct";
import Reviews from "../Reviews/Reviews";
import NavBar from "../../components/NavBar/NavBar";
import Swal from "sweetalert2";
import Loaderdots from "../../components/LoaderDots/Loaderdots";
import NoData from "../../components/NoData/NoData";
import { toast } from "react-toastify";
import { BeatLoader } from "react-spinners";
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { FaFacebookF, FaTwitter, FaWhatsapp, FaPinterestP, FaLink } from 'react-icons/fa';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import SizeGuid from "../SideGuide/SizeGuid";

function ProductDetails() {
  const { id } = useParams();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProduct] = useState([]);
  const [error, setError] = useState(null);
  const [selectedSize, setSelectedSize] = useState("");
  const [isDisable, setDisable] = useState(false);
  const [selectedColor, setColor] = useState("");
  const [stockStatusMessage, setStockStatusMessage] = useState("");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const touchStartX = useRef(null);
  // The carousel, so the add handler can measure the photo the visitor is
  // actually looking at and throw a clone of it into the cart.
  const carouselRef = useRef(null);

  const token = localStorage.getItem("userToken");
  const navigate = useNavigate();

  const shareUrl = window.location.href;
  const shareTitle = product?.name || "Check out this product!";
  const shareImage = product?.images[0]?.url
    ? `https://riymay.com/assets/${product.images[0].url}`
    : '';

  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "facebook-share-dialog", "width=626,height=436");
    setIsShareOpen(false);
  };
  const shareToTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`, "twitter-share-dialog", "width=626,height=436");
    setIsShareOpen(false);
  };
  const shareToWhatsApp = () => {
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareTitle} ${shareUrl}`)}`, "whatsapp-share-dialog", "width=626,height=436");
    setIsShareOpen(false);
  };
  const shareToPinterest = () => {
    window.open(`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&media=${encodeURIComponent(shareImage)}&description=${encodeURIComponent(shareTitle)}`, "pinterest-share-dialog", "width=626,height=436");
    setIsShareOpen(false);
  };

  useEffect(() => {
    fetch(`https://riymay.com/api/v1/user/products/${id}/reviews`)
      .then(r => r.json())
      .then(data => { setReviews(data.productReviewRating || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const checkStockStatus = (size, color) => {
    if (!size || !color) { setStockStatusMessage(""); return; }
    const match = product.stock.find(s => s.size === size && s.color === color);
    if (!match || match.stockStatus !== "inStock") setStockStatusMessage("Out of Stock");
    else if (parseInt(match.quantity, 10) === 0) setStockStatusMessage("Out of Stock");
    else setStockStatusMessage("In Stock");
  };

  useEffect(() => { checkStockStatus(selectedSize, selectedColor); }, [selectedSize, selectedColor]);

  const handleSizeChart = () => navigate("/size-guide", { state: { id } });

  useEffect(() => {
    fetch(`https://riymay.com/api/v1/user/products/product?id=${id}`)
      .then(res => { if (!res.ok) throw new Error("Failed to fetch"); return res.json(); })
      .then(data => { setProduct(data.product); setRelatedProduct(data.relatedProducts); })
      .catch(err => setError(err.message));
  }, [id]);

  const handleAddToCart = async (id) => {
    if (loading || isDisable) return;
    if (!token) {
      toast.error("Please Login to your account", { position: "top-right", autoClose: 3000, className: "custom-toast" });
      navigate("/login"); return;
    }
    if (!selectedSize || !selectedColor) {
      toast.warning("Please Select a Size & Color Before Adding to Cart.", { toastId: "CArtSize", position: "top-right", autoClose: 3000, theme: "colored" });
      return;
    }
    const matchingStock = product.stock.find(s => s.size === selectedSize && s.color === selectedColor && s.stockStatus === "inStock");
    if (!matchingStock || parseInt(matchingStock.quantity, 10) === 0) {
      toast.error(`The selected size (${selectedSize}) and color (${selectedColor}) is out of stock.`, { toastId: "utOfStack", autoClose: 3000, theme: "colored" });
      return;
    }
    // In-flight guard. This used to be set to true only AFTER a successful add
    // and was never cleared, so the button became a permanent spinner: a
    // customer could add one item and then could not add a second size or
    // colour of the same product without reloading the page. Set it before the
    // request and clear it in `finally`, which is what a loading flag is for.
    setDisable(true);
    try {
      const res = await fetch("https://riymay.com/api/v1/user/cart", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ size: selectedSize, color: selectedColor, productId: id }),
      });
      const data = await res.json();
      if (data.message === "email is not verfied") {
        try {
          const r = await fetch("https://riymay.com/api/v1/user/resend/verification/email", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ token }),
          });
          const d = await r.json();
          if (d.email) navigate("/otp-verification", { state: { email: d.email } });
          else toast.error(d.message || "Failed to resend OTP");
        } catch { toast.error("An error occurred while resending OTP"); }
        return;
      }
      if (data.message === "Invalid token" || data.message === "Invalid or expired token") {
        toast.info("Session Timeout", { position: "top-right", autoClose: false, closeOnClick: true });
        localStorage.removeItem("userToken");
        navigate("/login"); return;
      }
      localStorage.removeItem(`coupon_${id}_${selectedSize}`);

      // Was a toast that navigated to the cart when it closed — adding one item
      // ended the browsing session. The panel shows what went in and lets the
      // visitor choose.
      emitAddedToCart({
        name: product.name,
        image: product.images && product.images[0]
          ? `https://riymay.com/assets/${product.images[0].url}`
          : null,
        size: selectedSize,
        color: selectedColor,
      });

      // Tell the navbar so the cart icon can acknowledge it, and hand it the
      // photo plus where that photo currently sits on screen. The navbar
      // clones it and arcs it into the cart icon — the "small thing going
      // into the cart".
      //
      // The measurement happens HERE, synchronously, still inside the click.
      // Nothing is deferred to a frame or a timer: those have silently never
      // fired on this site before, and an animation that depends on them is an
      // animation that does not exist.
      let rect = null;
      const el = carouselRef.current
        && carouselRef.current.querySelectorAll(".pd-carousel-img")[slideIndex];
      if (el) {
        const r = el.getBoundingClientRect();
        // The track is a translated strip: only the visible slide is inside
        // the carousel frame. Measure the frame and keep the intersection so
        // the clone leaves from the square the visitor sees.
        const frame = carouselRef.current.getBoundingClientRect();
        const left = Math.max(r.left, frame.left);
        const top = Math.max(r.top, frame.top);
        const right = Math.min(r.right, frame.right);
        const bottom = Math.min(r.bottom, frame.bottom);
        if (right > left && bottom > top) {
          rect = { left, top, right, bottom, width: right - left, height: bottom - top };
        }
      }

      window.dispatchEvent(new CustomEvent("riymay:cart-changed", {
        detail: {
          image: product.images && product.images[0]
            ? `https://riymay.com/assets/${product.images[slideIndex] ? product.images[slideIndex].url : product.images[0].url}`
            : null,
          rect,
        },
      }));

    } catch {
      toast.error("An unexpected error occurred.");
    } finally {
      setDisable(false);
    }
  };

  // Mirrors the real layout, so nothing jumps when the product lands.
  if (loading) return <SkeletonProduct />;
  if (error) return <NoData />;
  if (!product) return <div className="loader-container"><div className="traffic-loader"></div></div>;

  return (
    <>
      <div className="pd-layout">

        {/* LEFT: swipe carousel — square frame, dots sit below the image */}
        <div className="pd-media">
        <div
          className="pd-carousel"
          ref={carouselRef}
          onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
          onTouchEnd={e => {
            if (touchStartX.current === null) return;
            const diff = touchStartX.current - e.changedTouches[0].clientX;
            if (diff > 50) setSlideIndex(i => Math.min(i + 1, product.images.length - 1));
            else if (diff < -50) setSlideIndex(i => Math.max(i - 1, 0));
            touchStartX.current = null;
          }}
        >
          <div
            className="pd-carousel-track"
            style={{ transform: `translateX(-${slideIndex * 100}%)` }}
          >
            {product.images.map((image, i) => (
              <img
                key={i}
                src={`https://riymay.com/assets/${image.url}`}
                alt={`${product.name} view ${i + 1}`}
                className="pd-carousel-img"
              />
            ))}
          </div>

          {/* Arrows */}
          {slideIndex > 0 && (
            <button className="pd-arrow pd-arrow-left" onClick={() => setSlideIndex(i => i - 1)}>
              <BsChevronLeft size={18} />
            </button>
          )}
          {slideIndex < product.images.length - 1 && (
            <button className="pd-arrow pd-arrow-right" onClick={() => setSlideIndex(i => i + 1)}>
              <BsChevronRight size={18} />
            </button>
          )}

        </div>

          {/* Dots — below the image, not overlaid on it */}
          {product.images.length > 1 && (
            <div className="pd-dots">
              {product.images.map((_, i) => (
                <span
                  key={i}
                  className={`pd-dot ${i === slideIndex ? "pd-dot-active" : ""}`}
                  onClick={() => setSlideIndex(i)}
                />
              ))}
            </div>
          )}
        </div>

        {/* RIGHT: sticky info panel */}
        <div className="pd-info">
          <h1 className="pd-name">{product.name}</h1>
          <p className="pd-price">₹ {product.price}</p>

          <div className="pd-divider" />

          {/* Size */}
          <div className="pd-section">
            <div className="pd-section-header">
              <span className="pd-label">Size</span>
              <button className="pd-size-guide-btn" onClick={handleSizeChart}>Size Guide</button>
            </div>
            <div className="pd-sizes">
              {product.availableSizes.map(size => (
                <button
                  key={size}
                  className={`pd-size-btn ${selectedSize === size ? "pd-size-selected" : ""}`}
                  onClick={() => setSelectedSize(size)}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {/* Color */}
          <div className="pd-section">
            <span className="pd-label">Color</span>
            <div className="pd-colors">
              {product.availableColors.map((color, i) => (
                <span
                  key={i}
                  onClick={() => setColor(color)}
                  className={`pd-color-swatch ${selectedColor === color ? "pd-color-selected" : ""}`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {stockStatusMessage && (
            <p className={`pd-stock ${stockStatusMessage === "In Stock" ? "pd-in-stock" : "pd-out-stock"}`}>
              {stockStatusMessage}
            </p>
          )}

          <button
            className={`pd-add-btn ${stockStatusMessage === "In Stock" ? "pd-add-active" : "pd-add-disabled"}`}
            onClick={() => handleAddToCart(product._id)}
          >
            {isDisable
              ? <BeatLoader size={8} color="#E8EBD6" />
              : !selectedSize
                ? "Select a size"
                : !selectedColor
                  ? "Select a colour"
                  : stockStatusMessage === "In Stock"
                    ? "Add to Cart"
                    : "Out of stock"}
          </button>

          <div className="pd-divider" />

          {/* Details */}
          <div className="pd-section">
            <span className="pd-label">Details</span>
            <p className="pd-desc">{product.description}</p>
          </div>

          {product.washCare && (
            <div className="pd-block">
              <p className="pd-block-label">Wash Care</p>
              <p className="pd-block-text">{product.washCare}</p>
            </div>
          )}

          {product.shipping && (
            <div className="pd-block">
              <p className="pd-block-label">Shipping</p>
              <p className="pd-block-text">{product.shipping}</p>
            </div>
          )}

          <div className="pd-section">
            <span className="pd-label">Category</span>
            <p className="pd-desc">{product.category}</p>
          </div>

          <div className="pd-divider" />

          {/* Share */}
          <div className="pd-share-wrap">
            <button className="pd-share-btn" onClick={() => setIsShareOpen(!isShareOpen)}>
              <FaLink size={11} /> Share
            </button>
            {isShareOpen && (
              <div className="pd-share-menu">
                <button className="pd-share-opt" onClick={shareToFacebook}><FaFacebookF size={13} /><span>Facebook</span></button>
                <button className="pd-share-opt" onClick={shareToTwitter}><FaTwitter size={13} /><span>Twitter</span></button>
                <button className="pd-share-opt" onClick={shareToWhatsApp}><FaWhatsapp size={13} /><span>WhatsApp</span></button>
                <button className="pd-share-opt" onClick={shareToPinterest}><FaPinterestP size={13} /><span>Pinterest</span></button>
                <CopyToClipboard text={shareUrl} onCopy={() => { setCopied(true); setTimeout(() => { setCopied(false); setIsShareOpen(false); }, 1500); }}>
                  <button className="pd-share-opt"><FaLink size={13} /><span>{copied ? "Copied!" : "Copy Link"}</span></button>
                </CopyToClipboard>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Related products */}
      <div className="pd-related">
        <h2 className="pd-related-heading">Related Products</h2>
        <div className="pd-related-grid">
          {relatedProducts.length > 0
            ? relatedProducts.map(p => (
                <Link className="related-product-container" key={p._id} to={`/product/${p._id}`}>
                  <RelatedProduct key={p._id} product={p} />
                </Link>
              ))
            : <p style={{ fontSize: 13, color: "rgba(31,70,41,0.4)" }}>No related products found.</p>
          }
        </div>
      </div>

      {/* Reviews */}
      <div className="pd-reviews">
        <h2 className="pd-reviews-heading">Ratings &amp; Reviews</h2>
        <Reviews productId={id} reviews={reviews} />
      </div>
    </>
  );
}

export default ProductDetails;
